/** A point on the screen, in CSS pixels. */
export interface IPoint {
  x: number;
  y: number;
}

/** A box on the screen, in CSS pixels (a `DOMRect`'s fields). */
export interface IRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** The click that may open a card's details as a flight. */
export interface IFlightClick {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  /** The carousel swallowed it (a swipe, an arrow). */
  defaultPrevented: boolean;
}

interface IPointArc {
  begin: IPoint;
  end: IPoint;
  /** Set only when the move is off both axes; on an axis the point moves in a line. */
  circle: {
    center: IPoint;
    radius: number;
    beginAngle: number;
    endAngle: number;
  } | null;
}

type Corner = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

/** Flutter's `_kOnAxisDelta`: closer than this to an axis, the arc is a straight line. */
const ON_AXIS_DELTA = 2;
/** The phone layout ends at `md`. */
const PHONE_MAX_WIDTH = 768;

/** Flutter's `_allDiagonals`, in its order: ties keep the first. */
const DIAGONALS: ReadonlyArray<readonly [Corner, Corner]> = [
  ["topLeft", "bottomRight"],
  ["bottomRight", "topLeft"],
  ["topRight", "bottomLeft"],
  ["bottomLeft", "topRight"],
];

const corner = (rect: IRect, which: Corner): IPoint => ({
  x: which.endsWith("Left") ? rect.left : rect.left + rect.width,
  y: which.startsWith("top") ? rect.top : rect.top + rect.height,
});

const center = (rect: IRect): IPoint => ({
  x: rect.left + rect.width / 2,
  y: rect.top + rect.height / 2,
});

/** Flutter's `MaterialPointArcTween`: a quarter-circle-ish path between two points. */
function pointArc(begin: IPoint, end: IPoint): IPointArc {
  const dx = end.x - begin.x;
  const dy = end.y - begin.y;
  const deltaX = Math.abs(dx);
  const deltaY = Math.abs(dy);
  if (deltaX <= ON_AXIS_DELTA || deltaY <= ON_AXIS_DELTA) {
    return { begin, end, circle: null };
  }

  const distanceSquared = dx * dx + dy * dy;
  const distance = Math.sqrt(distanceSquared);
  const sweep = (radius: number) => 2 * Math.asin(distance / (2 * radius));

  if (deltaX < deltaY) {
    const radius = distanceSquared / deltaX / 2;
    const circleCenter = {
      x: end.x + radius * Math.sign(begin.x - end.x),
      y: end.y,
    };
    const [beginAngle, endAngle] =
      begin.x < end.x
        ? [sweep(radius) * Math.sign(begin.y - end.y), 0]
        : [Math.PI + sweep(radius) * Math.sign(end.y - begin.y), Math.PI];
    return {
      begin,
      end,
      circle: { center: circleCenter, radius, beginAngle, endAngle },
    };
  }

  const radius = distanceSquared / deltaY / 2;
  const circleCenter = { x: begin.x, y: begin.y + Math.sign(dy) * radius };
  const beginAngle = begin.y < end.y ? -Math.PI / 2 : Math.PI / 2;
  const endAngle =
    beginAngle +
    sweep(radius) * (begin.y < end.y ? Math.sign(dx) : Math.sign(-dx));
  return {
    begin,
    end,
    circle: { center: circleCenter, radius, beginAngle, endAngle },
  };
}

function pointAt(arc: IPointArc, t: number): IPoint {
  if (t === 0) return arc.begin;
  if (t === 1) return arc.end;
  if (!arc.circle) {
    return {
      x: arc.begin.x + (arc.end.x - arc.begin.x) * t,
      y: arc.begin.y + (arc.end.y - arc.begin.y) * t,
    };
  }
  const { center: c, radius, beginAngle, endAngle } = arc.circle;
  const angle = beginAngle + (endAngle - beginAngle) * t;
  return {
    x: c.x + Math.cos(angle) * radius,
    y: c.y + Math.sin(angle) * radius,
  };
}

/** Material's `fastOutSlowIn`, `cubic-bezier(0.4, 0, 0.2, 1)`: the Hero flight's curve. */
const FAST_OUT_SLOW_IN = { x1: 0.4, y1: 0, x2: 0.2, y2: 1 };
/** Keyframes sampled along the arc; the browser interpolates linearly between them. */
const FLIGHT_STEPS = 24;

const bezier = (a: number, b: number, t: number) =>
  3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * b + t * t * t;

/** A CSS cubic-bezier's output at input `x` (bisection on the curve's x). */
function cubicBezier(curve: typeof FAST_OUT_SLOW_IN, x: number): number {
  if (x <= 0) return 0;
  if (x >= 1) return 1;
  let low = 0;
  let high = 1;
  for (let i = 0; i < 30; i++) {
    const mid = (low + high) / 2;
    if (bezier(curve.x1, curve.x2, mid) < x) low = mid;
    else high = mid;
  }
  return bezier(curve.y1, curve.y2, (low + high) / 2);
}

/** One sampled moment of a flight, as the two transforms the overlay animates. */
export interface IFlightKeyframe {
  /** On the clipping frame, sized to the header: moves and stretches it onto the box. */
  frame: string;
  /** On the photo inside: undoes the stretch, so the photo keeps its shape and covers. */
  photo: string;
}

/**
 * The app's card → details photo flight, ported from Flutter's `MaterialRectArcTween` (the
 * default `Hero` path): two opposite corners each travel their own arc, so a card on the
 * left, in the middle or on the right sweeps into the header along a different curve.
 */
export const heroFlightService = {
  /** The box at `t` (0 = `begin`, 1 = `end`); `t` is already eased. */
  rectAt(begin: IRect, end: IRect, t: number): IRect {
    const from = center(begin);
    const to = center(end);
    const move = { x: to.x - from.x, y: to.y - from.y };

    // Flutter's `_diagonalSupport`: the diagonal that best points along the move.
    let best: readonly [Corner, Corner] = ["topLeft", "bottomRight"];
    let bestSupport = -Infinity;
    for (const diagonal of DIAGONALS) {
      const a = corner(begin, diagonal[0]);
      const b = corner(begin, diagonal[1]);
      const length = Math.hypot(b.x - a.x, b.y - a.y) || 1;
      const support = (move.x * (b.x - a.x) + move.y * (b.y - a.y)) / length;
      if (support > bestSupport) {
        best = diagonal;
        bestSupport = support;
      }
    }

    const p = pointAt(
      pointArc(corner(begin, best[0]), corner(end, best[0])),
      t,
    );
    const q = pointAt(
      pointArc(corner(begin, best[1]), corner(end, best[1])),
      t,
    );
    return {
      left: Math.min(p.x, q.x),
      top: Math.min(p.y, q.y),
      width: Math.abs(q.x - p.x),
      height: Math.abs(q.y - p.y),
    };
  },

  /** Only a plain tap on a phone flies; a new-tab click, a swipe or a desktop click navigates as before. */
  shouldFly(
    click: IFlightClick,
    viewportWidth: number,
    prefersReducedMotion: boolean,
  ): boolean {
    return (
      click.button === 0 &&
      !click.metaKey &&
      !click.ctrlKey &&
      !click.shiftKey &&
      !click.altKey &&
      !click.defaultPrevented &&
      viewportWidth < PHONE_MAX_WIDTH &&
      !prefersReducedMotion
    );
  },

  /** Whether a box is on screen, so the photo can fly back into it. */
  isOnScreen(
    rect: IRect,
    viewport: { width: number; height: number },
  ): boolean {
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.left + rect.width > 0 &&
      rect.top + rect.height > 0 &&
      rect.left < viewport.width &&
      rect.top < viewport.height
    );
  },

  /** Material's `fastOutSlowIn` at `p` (0–1). */
  ease(p: number): number {
    return cubicBezier(FAST_OUT_SLOW_IN, p);
  },

  /**
   * The flight as transform keyframes, so the browser's compositor plays it and a busy
   * page (the next route loading) can't stall it. The frame is an `end`-sized box moved and
   * stretched onto each arc box; the photo inside scales back to an even size that covers
   * the box, centred. Opening runs card → header on `fastOutSlowIn`; closing runs header →
   * card on its flip, as Flutter reverses a Hero.
   */
  flightKeyframes(
    card: IRect,
    end: IRect,
    direction: "open" | "close",
  ): IFlightKeyframe[] {
    const frames: IFlightKeyframe[] = [];
    for (let i = 0; i <= FLIGHT_STEPS; i++) {
      const p = i / FLIGHT_STEPS;
      const t =
        direction === "open"
          ? heroFlightService.ease(p)
          : 1 - heroFlightService.ease(p);
      const box = heroFlightService.rectAt(card, end, t);
      const sx = box.width / end.width || 1e-6;
      const sy = box.height / end.height || 1e-6;
      const cover = Math.max(sx, sy);
      const dx = ((sx - cover) * end.width) / 2 / sx;
      const dy = ((sy - cover) * end.height) / 2 / sy;
      frames.push({
        frame: `translate(${box.left - end.left}px, ${box.top - end.top}px) scale(${sx}, ${sy})`,
        photo: `translate(${dx}px, ${dy}px) scale(${cover / sx}, ${cover / sy})`,
      });
    }
    return frames;
  },
};
